/**
 * Created by Ning Jiang on 2023-12-12.
 */

import {DeadwoodRipStackedSymbol} from "./DeadwoodRipStackedSymbol";
import {DeadwoodRipSymbolState} from "./DeadwoodRipSymbolConfig";
import {DeadwoodRip} from "../DeadwoodRip";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {gsap, TimelineLite} from "gsap";
import {DeadwoodRipSymbol} from "./DeadwoodRipSymbol";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {DeadwoodRipGameAssets} from "../DeadwoodRipGameAssets";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {DeadwoodRipSound} from "../sound/DeadwoodRipSound";
import {DeadwoodRipLayers, Layers} from "../view/DeadwoodRipLayers";

export class DeadwoodRipStackedWild extends DeadwoodRipStackedSymbol {

    protected _ripAssetName:string = "xRip_4";

    constructor(symbolName:string, reelId:number, initState:DeadwoodRipSymbolState) {
        super(symbolName, reelId, initState);
    }

    public getSymbolShotRect():PIXI.Rectangle {
        return new PIXI.Rectangle(this._centerPosition.x - 50, this._centerPosition.y - 266, 100, 532);
    }

    protected getStateTexture(state?:DeadwoodRipSymbolState):PIXI.Texture[] {
        // if WXN is the stacked symbol with different looks on different reels, then this will make the WXN on reel 1 to load image WXN1/WXN1.png
        let texturePath = `${this.stackedSymbolName.symName}${this.reelId}/${this.stackedSymbolName.symName}${this.reelId}`;
        if(state === DeadwoodRipSymbolState.SPIN) {
            texturePath += `_${state}`;
        }

        return DeadwoodRip.assetLoader.getTextures(texturePath);
    }

    protected initAnimation() {
        super.initAnimation();
        this._multiplierContainer.position.set(this._centerPosition.x, this._centerPosition.y + DeadwoodRipSymbol.DEFAULT_HEIGHT * 1.5);
    }

    protected getMultiplierText():PIXI.BitmapText {
        const text = new PIXI.BitmapText("", {
            fontName : DeadwoodRipGameAssets.BITMAP_FONT_NUDGE_MULTIPLIER,
            fontSize : 42,
            tint : 0xffffff
        });
        (<PIXI.Point>text.anchor).set(0.5, 1.36);
        text.letterSpacing = -32;
        return text;
    }

    protected getOnShotAnimationName():string {
        return "4xWILD_Shoot";
    }

    protected getKillAnimationName():string {
        return `WXN${this.reelId}Dead`;
    }

    protected getDeadLoopAnimationName():string {
        return `WXN${this.reelId}DeadLoop`;
    }

    protected getIndividualAnimationName():string {
        return `WXN${this.reelId}Toggle`;
    }

    protected getCharacterAssetName():string {
        return "wxn";
    }

    public updateMultiplier(value:number, animate:boolean):TimelineLite {
        Logger.logDev(`wild update to multiplier ${value}`);
        const tl = new TimelineLite();
        if(value === this.currentMultiplier) {
            return tl;
        }

        (<DeadwoodRipStackedWild[]>this.stack?.symbols).forEach((w:DeadwoodRipStackedWild) => {
            if(w) {
                w._currentMultiplier = value;
            }
        })

        tl.add(() => {
            (<DeadwoodRipStackedWild[]>this.stack?.symbols).forEach((w:DeadwoodRipStackedWild) => {
                if(w) {
                    w._multiplierText.text = `${DeadwoodRip.model.data.mode === GameMode.NORMAL ? "+" : "x"}${value}`;
                    w._multiplierText.scale.set(1);
                    w._multiplierText.updateTransform();
                    if(w._multiplierText.textWidth > 130) {
                        w._multiplierText.scale.x = MathHelper.roundToDecimals(130 / w._multiplierText.textWidth, 2);
                    }
                }
            })
        }, 0);

        if(animate) {
            // this._multiplierGlow.texture = DeadwoodRip.assetLoader.getTextures(`wild/multiplier/x${value}`)[0];
            tl.add([
                gsap.to(this._multiplierContainer.scale, {x : 1.2, y : 1.2, duration : 0.1}),
                // gsap.to(this._multiplierGlow, {alpha : 1, duration : 0.1})
            ]);
            tl.add([
                gsap.to(this._multiplierContainer.scale, {x : 1, y : 1, duration : 0.5, ease : "power3.out"}),
                // gsap.to(this._multiplierGlow, {alpha : 0, duration : 0.5})
            ]);
        }

        return tl;
    }

    protected addSplitSymbolAnimation(tl:TimelineLite, oldSymbolSize:number, type:number, noWin:boolean):void {
        if(!noWin) {
            const splitSpine = new PIXI.spine.Spine(DeadwoodRip.assetLoader.getSpineAsset("split"));
            splitSpine.autoUpdate = true;
            const globalPosition = this.getGlobalCenterPosition();
            splitSpine.position.set(globalPosition.x + this._centerPosition.x, globalPosition.y + this._centerPosition.y + DeadwoodRipSymbol.DEFAULT_HEIGHT * 3);
            tl.add(() => {
                DeadwoodRipLayers.getLayer(Layers.WIN_PRESENTATIONS)!.addChild(splitSpine);
                splitSpine.state.setAnimation(0, "animation", false);
            }, 0);
            tl.add(() => {
                splitSpine.parent.removeChild(splitSpine);
                splitSpine.destroy();
            }, splitSpine.spineData.findAnimation("animation").duration);
        }

        tl.add([
            this.updateMultiplier(this.currentMultiplier * 2, this.visible),
            () => {
                this.updateLayers();
            }
        ], noWin ? 0.01 : this.splitSplashDelay);
        this._parts.forEach(p => {
            tl.add(gsap.to(p, {alpha : 0, duration : 0.2}), this.splitSplashDelay);
        });
    }

    public cylinderSplit(reelId:number, symbolId:number):TimelineLite {
        const tl = new TimelineLite();
        tl.add(this.updateMultiplier(this.currentMultiplier * 2, true));
        this._parts.forEach(p => {
            tl.add(gsap.to(p, {alpha : 0, duration : 0.2}), this.splitSplashDelay);
        });
        return tl;
    }

    public playLanding(init:boolean = false):TimelineLite {
        const tl = new TimelineLite();
        if(init && !this.visible) {
            return tl;
        }

        this.createDeadContainer();
        const aniName = `WXN${this.reelId}_land`;
        tl.add(() => {
            this._deadCharacter.visible = true;
            this._deadCharacter.state.setAnimation(1, aniName, false);
        });
        if(!init) {
            tl.add([
                this.updateMultiplier(1, true),
                () => {
                    const sound = DeadwoodRipSound.soundPlayer.playTrack({name : DeadwoodRipSound.NUDGE_LAND[this.reelId - 1]});
                    if(sound) {
                        sound.player.stereo(DeadwoodRipSound._davidPanningReelStop[this.reelId]);
                    }
                }
            ]);
        }
        tl.add(() => {}, `${this._deadCharacter.spineData.findAnimation(aniName).duration}`);
        //     if(!init) {
        //         this.shotSymbol(this.getLandingShotPosition());
        //     }

        this._parts.forEach(p => {
            tl.add(gsap.to(p, {alpha : 0, duration : 0.2}), 0);
        });


        return tl;
    }

    public getNudgeAnimation(lastStep:boolean, aborted:boolean, hasWin:boolean):TimelineLite {
        this.createDeadContainer();
        const tl = new TimelineLite();
        let aniName:string = `WXN${this.reelId}_nudge`;
        if(aniName.length > 0) {
            tl.add(() => {
                this._deadCharacter.visible = true;
                this._deadCharacter.state.setAnimation(1, aniName, false);
            });
            tl.add(() => {}, `+=${this._deadCharacter.spineData.findAnimation(aniName).duration}`);
        }
        this._parts.forEach(p => {
            tl.add(gsap.to(p, {alpha : 0, duration : 0.2}), 0);
        });

        return tl;
    }

    public getNudgeEndAnimation():TimelineLite {
        this._isKilled = false;
        this.createDeadContainer();
        const tl = new TimelineLite();
        let aniName = `WXN${this.reelId}_lastNudge`;
        tl.add(() => {
            this._deadCharacter.visible = true;
            // this._deadCharacter.state.setEmptyAnimation(0, 0);
            // this._deadCharacter.state.clearTrack(0);
            // this._deadCharacter.skeleton.setToSetupPose();
            this._deadCharacter.state.setAnimation(1, aniName, false);
        });
        tl.add(() => {}, `+=${this._deadCharacter.spineData.findAnimation(aniName).duration}`);
        this._parts.forEach(p => {
            tl.add(gsap.to(p, {alpha : 0, duration : 0.2}), 0);
        });
        return tl;
    }

    protected playKillSound():void {
        DeadwoodRipSound.playSymbolKill(`WXN${this.reelId}`, this.reelId);
    }
}